import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';
import { VendorDetailsStep } from './VendorDetailsStep';
import { VendorPatchUpdateRequestDocuments } from '@brainvaulttech/dgr-react-sdk';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Loader from '@/components/Loader';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { genUniqueId } from '@/lib/utils';
import { useContext, useEffect, useState } from 'react';
import AlertBox from '@/components/AlertBox';
import { Button } from '@/components/ui/button';
import { uploadMedia } from '@/services/uploadMedia';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import { displayError } from '@/lib/error';
import { toast } from '@/components/ui/use-toast';
import ToolBar from '@/components/ToolBar';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { DocumentType, vendorContactDetailsSchema, vendorDetailsSchema, vendorSchema } from './vendorSchema';
import { VendorContactCreateRequest, VendorContactUpdateRequest, VendorPatchUpdateRequest } from '@/types/vendor.types';
import { useGetVendorById, useUpdateVendorMutation } from '@/services/vendorService';
import Stepper from '@/components/Stepper';
import { ContactForm } from '@/components/ContactForm';

const schemas = [vendorDetailsSchema, vendorContactDetailsSchema];
export const VendorUpdate = () => {
  const { vendorId } = useParams();
  const [isAlertBoxOpen, setIsAlertBoxOpen] = useState(false);
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState(false);
  const [documents, setDocuments] = useState<DocumentType[]>([
    { id: genUniqueId(), name: '', document: null, mediaId: null },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentUploadLoading, setIsDocumentUploadLoading] = useState(false);
  const [searchParam, setSearchParam] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [currentSchema, setCurrentSchema] = useState(schemas[activeStep]);
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();
  const updateVendorMutation = useUpdateVendorMutation();
  const { data, isLoading: isVendorLoading, isError } = useGetVendorById(vendorId);

  const form = useForm<vendorSchema>({
    resolver: zodResolver(currentSchema),
    mode: 'onBlur',
  });

  const {
    formState: { isValid, isDirty },
    getValues,
    trigger,
  } = form;

  const { setIsDirty } = useContext(NavigationBlockerContext);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (data) {
      const vendorData = data;
      form.reset(
        {
          name: vendorData.name,
          address1: vendorData.address1,
          address2: vendorData.address2,
          city: vendorData.city,
          state: vendorData.state,
          zipCode: vendorData.zipCode,
          phone: vendorData.phone,
          email: vendorData.email,
          fees: vendorData.fees.toString(),
          taxId: vendorData.taxId,
          coverageAreas: vendorData.coverageAreas.join(', '),
          notes: vendorData.notes ?? '',
          status: vendorData.status,
          documents: vendorData.documents.map((doc) => ({
            id: genUniqueId(),
            name: doc.name,
            document: undefined,
            mediaId: doc.media?.mediaId ?? null,
            mediaData: doc ?? null,
          })),
          contacts: vendorData.vendorContacts.map((contact) => ({
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone,
            contactId: contact.vendorContactId,
          })),
        },
        { keepDirty: false }
      );
      if (vendorData.documents.length === 0) {
        setDocuments([{ id: genUniqueId(), name: '', document: null, mediaId: null }]);
      } else {
        setDocuments(
          vendorData.documents.map((doc) => ({
            id: genUniqueId(),
            name: doc.name,
            document: undefined,
            mediaId: doc.media?.mediaId ?? null,
            mediaData: doc ?? null,
          }))
        );
      }
    }
    setRefreshKey((prev) => prev + 1);
  }, [data]);

  const handleSubmit = async () => {
    if (!vendorId) return;
    setIsLoading(true);
    setIsDocumentUploadLoading(true);
    const updatedDocuments = await Promise.all(
      documents
        .filter((doc) => doc.document ?? doc.mediaId)
        .map(async (doc) => {
          if (!doc.mediaId && doc.document && doc.document instanceof File) {
            const media = await uploadMedia(doc.document, 'VENDOR');
            return { ...doc, mediaId: media?.mediaId };
          }
          return doc;
        })
    );
    setDocuments(updatedDocuments);
    setIsDocumentUploadLoading(false);
    if (updatedDocuments.some((doc) => !doc.mediaId)) return;

    const originalDocs = data?.documents ?? [];
    const docData: VendorPatchUpdateRequestDocuments = { create: [], update: [], delete: [] };
    for (const doc of updatedDocuments) {
      const isExisting = doc.mediaData !== undefined;
      const originalDoc = isExisting
        ? originalDocs.find((d) => d.vendorDocumentId === doc.mediaData?.vendorDocumentId)
        : null;

      if (!isExisting && doc.mediaId && docData.create) {
        docData.create.push({
          name: doc.name,
          mediaId: doc.mediaId,
        });
      } else if (
        doc?.mediaData?.vendorDocumentId &&
        isExisting &&
        docData.update &&
        (doc.name !== originalDoc?.name || doc.mediaId !== originalDoc?.media?.mediaId)
      ) {
        docData.update.push({
          vendorDocumentId: doc?.mediaData?.vendorDocumentId,
          name: doc.name,
          mediaId: doc.mediaId!,
        });
      }
    }
    for (const originalDoc of originalDocs) {
      const stillExists = updatedDocuments.some((d) => d.mediaData?.vendorDocumentId === originalDoc.vendorDocumentId);
      if (!stillExists && originalDoc.media?.mediaId && docData.delete && originalDoc?.vendorDocumentId) {
        docData.delete.push({ vendorDocumentId: originalDoc?.vendorDocumentId });
      }
    }

    const dirtyFields = form.formState.dirtyFields;
    const formData = form.getValues();
    const originalContacts = data?.vendorContacts ?? [];
    let contactData: { create: VendorContactCreateRequest[]; update: VendorContactUpdateRequest[] } | undefined;

    if (dirtyFields.contacts) {
      contactData = { create: [], update: [] };
      const contacts = getValues().contacts;

      const newContacts = contacts?.filter((contact) => !contact.contactId) ?? [];
      contactData.create = newContacts.map((contact) => ({
        firstName: contact.firstName ?? undefined,
        lastName: contact.lastName ?? undefined,
        email: contact.email ?? undefined,
        phone: getPhoneNumberValue(contact.phone) ?? undefined,
      }));

      const existingContacts = contacts?.filter((contact) => contact.contactId) ?? [];
      contactData.update = existingContacts
        .map((contact) => {
          const originalContact = originalContacts.find((c) => c.vendorContactId === contact.contactId);
          if (contact.contactId) {
            const updateData: VendorContactUpdateRequest = {
              vendorContactId: contact.contactId,
            };
            if (originalContact) {
              if (originalContact?.firstName !== contact.firstName) {
                updateData.firstName = contact?.firstName ?? undefined;
              }
              if (originalContact?.lastName !== contact.lastName) {
                updateData.lastName = contact?.lastName ?? undefined;
              }
              if (originalContact?.email !== contact.email) {
                updateData.email = contact?.email ?? undefined;
              }
              if (originalContact?.phone !== getPhoneNumberValue(contact.phone)) {
                updateData.phone = getPhoneNumberValue(contact.phone) ?? null;
              }
            }

            if (Object.keys(updateData).length > 1) return updateData;
          }
          return null;
        })
        .filter((contact): contact is VendorContactUpdateRequest => contact !== null);
    }
    const dirtyData: VendorPatchUpdateRequest = {};

    Object.keys(dirtyFields).forEach((key) => {
      const typedKey = key as keyof vendorSchema;
      if ((dirtyFields as Record<string, boolean>)[typedKey]) {
        if (typedKey === 'coverageAreas') {
          dirtyData[typedKey] = formData.coverageAreas
            .split(',')
            .map((area) => area.trim())
            .filter((area) => area);
        } else if (typedKey === 'fees') {
          dirtyData[typedKey] = Number(formData.fees);
        } else if (typedKey === 'phone') {
          dirtyData[typedKey] = getPhoneNumberValue(formData.phone);
        } else if (typedKey === 'documents') {
          dirtyData[typedKey] = {
            create: docData.create ?? [],
            update: docData.update ?? [],
            delete: docData.delete ?? [],
          };
        } else if (typedKey !== 'contacts') {
          dirtyData[typedKey] = formData[typedKey] ?? '';
        }
      }
    });

    updateVendorMutation.mutate(
      { vendorId, vendorData: { ...dirtyData, vendorContacts: contactData } },
      {
        onSuccess: (data) => {
          setIsAlertBoxOpen(false);
          setIsLoading(false);
          navigate('/vendors');
          toast({
            variant: 'default',
            description: data?.message ?? 'Vendor updated successfully',
          });
        },
        onError: (e) => {
          setIsLoading(false);
          setIsAlertBoxOpen(false);
          displayError(e.response?.data);
        },
      }
    );
  };
  const [steps] = useState([
    { title: 'Vendor Details', isCompleted: true },
    { title: 'Vendor Contacts', isCompleted: true },
  ]);
  useEffect(() => {
    const activeStep = searchParam.get('stepper');
    if (activeStep) {
      const index = steps.findIndex((step) => step.title === activeStep);
      setActiveStep(index);
    }
  }, [searchParam]);
  useEffect(() => {
    setCurrentSchema(schemas[activeStep]);
  }, [activeStep]);

  const goToStep = async (step: number) => {
    const isValid = await trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        setSearchParam({ stepper: steps[step].title });
        setActiveStep(step);
      }
    }
  };

  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="vendor">
      <Loader isLoading={isVendorLoading}>
        <ToolBar title={data?.name} />
        <div className="flex flex-col gap-6 w-full">
          <div className="mt-3 flex justify-center pl-24">
            <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} mode="edit" />
          </div>
          <FormProvider {...form}>
            {activeStep === 0 && (
              <VendorDetailsStep mode="EDIT" documents={documents} setDocuments={setDocuments} key={refreshKey} />
            )}
            {activeStep === 1 && (
              <ContactForm mode="edit" isFetching={isLoading} key={refreshKey} contactFor="VENDOR" />
            )}
          </FormProvider>
          <div className={`flex flex-row justify-end gap-6 pb-8`}>
            <AlertBox
              triggerButton={
                <Button
                  type="button"
                  variant="outline"
                  className=" w-[75px] h-[36px]"
                  onClick={() => setIsCancelAlertOpen(true)}
                  data-testid="trigger-cancel-button"
                >
                  Cancel
                </Button>
              }
              title="Are you sure you want to cancel?"
              description={'All of your current changes will be lost.'}
              warning={true}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              cancelBtnClick={() => {
                setIsCancelAlertOpen(false);
              }}
              confirmBtnClick={() => {
                setIsCancelAlertOpen(false);
                navigate('/vendors');
              }}
              open={isCancelAlertOpen}
            />
            <AlertBox
              triggerButton={
                <Button
                  variant="default"
                  className="h-[36px] w-[103px] hover:bg-[#12375C]"
                  disabled={!isValid || Object.keys(form.formState.dirtyFields).length === 0}
                  onClick={() => setIsAlertBoxOpen(true)}
                  data-testid="trigger-save-button"
                >
                  Save
                </Button>
              }
              title={`Are you sure?`}
              description={`Would you like to update ${data?.name}’s details?`}
              cancelBtnLabel="No"
              confirmBtnLabel="Yes"
              open={isAlertBoxOpen}
              isLoading={isDocumentUploadLoading || updateVendorMutation.status === 'pending' || isLoading}
              cancelBtnClick={() => setIsAlertBoxOpen(false)}
              confirmBtnClick={handleSubmit}
            />
          </div>
        </div>
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};
